import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import settingsAtom, { fetchBlocks, getProjectList } from '../../atoms/settings/SettingsAtom';
import ProjectList from './ProjectList';
import { HeaderList } from '../constent/constent';
import Cookies from "js-cookie";

const settings = ['Profile', 'Settings', 'Logout'];

const ResponsiveAppBar = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate()

  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [data] = useAtom(settingsAtom);
  const [, setProjectList] = useAtom(getProjectList);
  const [, setBlocks] = useAtom(fetchBlocks);
  const [projectId] = useState(() => localStorage.getItem('project_id'));

  useEffect(() => {
    const token = Cookies.get("access_token");
    if (token) {
      setProjectList();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (projectId) {
      setBlocks(projectId);
    }
     // eslint-disable-next-line
  }, [data?.projectList, projectId]);


  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const isActive = (page) => location.pathname.toLowerCase().includes(page?.path);
  const handleProfileClick = () => {
    handleCloseUserMenu();
  };

  const handleSettingsClick = () => {
    handleCloseUserMenu();
    navigate('/settings');
  };

  const handleLogoutClick = () => {
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
    localStorage.clear();
    navigate('/login');
    handleCloseUserMenu();
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: 'white',
        color: 'black',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }}
    >
      <Container maxWidth="xxl" disableGutters sx={{ px: '16px' }}>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }} >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="/suryalogo.svg"
              alt="logo"
              sx={{ width: { xs: 120, md: 175 }, mr: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>

              {isMobile && (
                <>
                  <IconButton
                    size="large"
                    aria-label="menu"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    keepMounted
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                  >
                    {HeaderList.map((page) => (
                      <MenuItem
                        key={page?.key}
                        onClick={handleCloseNavMenu}
                        component={Link}
                        to={`${page?.path}`}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#fff2f1',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {page?.icon}
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ fontSize: '17px' }}
                          >
                            {page?.label}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              {!isMobile && (
                <Box sx={{ display: 'flex', ml: 3 }}>
                  {HeaderList.map((page) => {
                    const active = isActive(page);
                    return (
                      <Button
                        key={page?.key}
                        component={Link}
                        to={`${page?.path}`}
                        onClick={handleCloseNavMenu}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#fff2f1',
                            color: 'primary.main',
                          },
                          color: active ? 'primary.main' : 'black',
                          borderBottom: active ? '2px solid' : 'none',
                          borderColor: active ? 'primary.main' : 'transparent',
                          height: '64px',
                          borderRadius: 0,
                          px: 2,
                          fontWeight: active ? 600 : 400,
                          textTransform: 'none',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {page?.icon}
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              fontWeight: active ? 600 : 400,
                              fontSize: '17px',
                            }}
                          >
                            {page?.label}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>


          {/* Project List and Avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isMobile && <ProjectList list={data?.projectList} />}
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {data?.userDetails?.profile_data?.first_name?.charAt(0).toUpperCase() || ''}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}

              >
                {settings.map((setting) => {
                  let onClickHandler = null;
                  if (setting === 'Profile') onClickHandler = handleProfileClick;
                  else if (setting === 'Settings') onClickHandler = handleSettingsClick;
                  else if (setting === 'Logout') onClickHandler = handleLogoutClick;
                  return (
                    <MenuItem
                      sx={{ width: '200px' }}
                      key={setting}
                      onClick={onClickHandler}
                    >
                      <Typography textAlign="center">{setting}</Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ResponsiveAppBar;